/**
 * Subscription service for checking user subscription status
 */

import { httpWithAuth } from './apiClient';

interface SubscriptionStatus {
  has_active_subscription: boolean;
  subscription_id: string | null;
  plan_id: string | null;
  provider: 'stripe' | 'marketplace' | null;
  status: string;
  slides_used: number;
  slides_limit: number;
  is_read_only: boolean;
  period_end: string | null;
}

interface CustomerPortalResponse {
  portal_url: string;
}


/**
 * Check if user has an active subscription
 */
export const checkSubscriptionStatus = async (): Promise<SubscriptionStatus | null> => {
  try {
    const data = await httpWithAuth<SubscriptionStatus>('/api/subscription/status', {
      method: 'GET',
    });
    return data;
  } catch (error) {
    console.error('Error checking subscription status:', error);
    // Return null for any error (network, 401, etc.)
    // 401 errors are automatically handled by global handler
    return null;
  }
};

/**
 * Check if user needs to be redirected to pricing page
 */
export const shouldRedirectToPricing = async (): Promise<boolean> => {
  const status = await checkSubscriptionStatus();

  if (!status) {
    // If we can't check status, redirect to pricing (safer default)
    // This handles cases where user_active_subscription doesn't exist yet
    return true;
  }

  // Redirect if user has no active subscription
  return !status.has_active_subscription;
};

/**
 * Open Stripe Customer Portal for subscription management
 *
 * @description Opens the Stripe Customer Portal where users can:
 * - Cancel their subscription
 * - Change subscription plans (upgrade/downgrade)
 * - Update payment methods
 * - View billing history
 *
 * @returns Promise that resolves when portal URL is obtained and redirect initiated
 * @throws Error if user is not authenticated or has no Stripe customer record
 */
export const openCustomerPortal = async (): Promise<void> => {
  try {
    const data = await httpWithAuth<CustomerPortalResponse>('/api/subscription/customer-portal', {
      method: 'POST',
    });

    // Redirect to Stripe Customer Portal
    window.location.href = data.portal_url;
  } catch (error) {
    console.error('Error opening customer portal:', error);
    throw new Error('Failed to open customer portal. Please try again.');
  }
};
